/**
 * First-touch marketing attribution, for the signup path.
 *
 * Writes the same `attribution` map gobo-tix writes onto orders, carts and
 * waitlist entries — see `docs/attribution-schema.md` in that repo, which is
 * the contract reporting codes against. Same keys, same omit-when-absent rule,
 * same 200-character cap, same first-touch-wins model.
 *
 * ⚠️ **Stored in a COOKIE, not localStorage.** gobo-tix uses localStorage,
 * which it can because it is a single host. Signups are not: people land on
 * **www.gobo.show** and finish on **app.gobo.show**, and localStorage does not
 * cross subdomains — a first touch written on the marketing site would be
 * invisible here. A cookie scoped to `.gobo.show` does cross, which is the
 * same reason `_fbc` works today (lib/meta-pixel.ts writes it the same way).
 *
 * One field beyond the order schema: **`fbc`**, the Meta click cookie
 * (`fb.1.{ts}.{fbclid}`). It is what the Conversions API matches on, and
 * unlike `fbclid` it survives the hop from www to app on its own. Orders do
 * NOT carry it — the schema doc notes the difference.
 *
 * No imports, so test/attribution.test.ts can run it under Node's type
 * stripping — the same reason lib/festivalSpaces.ts has none. Errors are
 * handed to an optional `onError` rather than reported here, so the caller
 * owns Bugsnag and nothing is swallowed silently.
 */

/** Where an unexpected failure goes. Callers pass `logError`. */
type ErrorSink = (error: unknown, meta?: Record<string, unknown>) => void;

const COOKIE_NAME = 'gobo_attribution';
const TTL_DAYS = 30;
const MAX_VALUE_LENGTH = 200;

/** Parameters that, when present, start a first touch. Matches gobo-tix. */
const TRACKING_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
] as const;

export interface Attribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  fbclid?: string;
  /** The Meta click cookie. Not present on orders — see the note above. */
  fbc?: string;
  referrer?: string;
  landingPage?: string;
}

function clean(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.slice(0, MAX_VALUE_LENGTH);
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  for (const part of document.cookie.split(';')) {
    const [key, ...rest] = part.trim().split('=');
    if (key === name) {
      try {
        return decodeURIComponent(rest.join('='));
      } catch {
        return null;
      }
    }
  }
  return null;
}

/** The stored first touch, or null when there isn't one or it's unreadable. */
function readStored(): (Attribution & { capturedAt?: number }) | null {
  const raw = readCookie(COOKIE_NAME);
  if (!raw) return null;
  try {
    const stored = JSON.parse(raw);
    if (!stored || typeof stored !== 'object') return null;
    return stored;
  } catch {
    // Malformed value — treat as absent. Attribution is never worth breaking
    // signup over.
    return null;
  }
}

/**
 * Record a first touch when the URL carries tracking parameters and no
 * unexpired one exists. Safe to call on every mount and route change: it is a
 * no-op once a touch is recorded, and a no-op when there is nothing to record.
 *
 * The cookie's own max-age enforces the 30-day window, so there is no
 * expiry check to get wrong — the browser simply stops sending it.
 */
export function captureAttribution(search?: string, onError?: ErrorSink): void {
  if (typeof window === 'undefined') return;
  try {
    const query = new URLSearchParams(
      search === undefined ? window.location.search : search
    );

    const captured: Attribution = {};
    for (const param of TRACKING_PARAMS) {
      const value = clean(query.get(param));
      if (value) captured[param] = value;
    }

    // Nothing to attribute — leave any existing first touch alone.
    if (Object.keys(captured).length === 0) return;

    // First touch wins. A later campaign does not overwrite it, so a signup is
    // credited to whatever originally brought the person in.
    if (readStored()) return;

    const referrer = clean(document.referrer);
    if (referrer) captured.referrer = referrer;

    const landingPage = clean(window.location.pathname + window.location.search);
    if (landingPage) captured.landingPage = landingPage;

    const payload = JSON.stringify({ ...captured, capturedAt: Date.now() });
    const maxAge = 60 * 60 * 24 * TTL_DAYS;
    // domain=.gobo.show is what lets the marketing site's touch reach signup.
    // Browsers silently drop a parent-domain cookie on other hostnames such as
    // localhost, which is the right local-dev behaviour — see
    // docs/meta-tracking.md.
    document.cookie =
      `${COOKIE_NAME}=${encodeURIComponent(payload)}; domain=.gobo.show; ` +
      `path=/; max-age=${maxAge}; secure; samesite=lax`;
  } catch (err) {
    // Storage disabled, a locked-down browser, an exotic URL. Attribution is
    // never worth breaking a page mount over — but it is reported.
    onError?.(err, { phase: 'capture' });
  }
}

/**
 * The attribution to write onto a document, or null when this visitor arrived
 * without anything to attribute.
 *
 * `capturedAt` is dropped — it is the cookie's bookkeeping, and the document's
 * own timestamps are what reporting reads.
 *
 * `fbc` is read LIVE from the `_fbc` cookie rather than from the stored touch:
 * that cookie is itself the durable cross-subdomain record, Meta may refresh
 * it, and lib/meta-pixel.ts writes it on any page carrying an `fbclid` —
 * possibly after this first touch was recorded.
 */
export function getAttribution(onError?: ErrorSink): Attribution | null {
  try {
    const stored = readStored();
    const attribution: Attribution = { ...(stored || {}) };
    delete (attribution as { capturedAt?: number }).capturedAt;

    const fbc = clean(readCookie('_fbc'));
    if (fbc) attribution.fbc = fbc;

    return Object.keys(attribution).length > 0 ? attribution : null;
  } catch (err) {
    // An unreadable cookie must not stop someone signing up.
    onError?.(err, { phase: 'read' });
    return null;
  }
}
